import axios from 'axios';
import { VIEW_ENTITY_TYPE_URL, get_request_config } from '@/utils/system_constants/constant.js';
import { fetch_roles as fetch_roles_service, fetch_users as fetch_users_service } from '@/services/user_service.js';
import {
  build_create_form,
  get_default_role,
  get_entity_type_id_value,
  get_entity_type_label_value,
  get_entity_type_project_id_value,
  has_valid_name,
  is_coordinator_role,
  is_valid_email,
  is_valid_phone_number,
  parse_api_data
} from './user_management_helpers';

export const user_management_core_methods = {
  parse_error_message(error, fallback) {
    const response_data = parse_api_data(error?.response?.data);
    return response_data.message || response_data.data || error.message || fallback;
  },

  reset_create_form() {
    this.create_form = build_create_form(this.roles);
  },

  get_entity_type_option_label(item = {}, index = 0) {
    return get_entity_type_label_value(item, index);
  },

  normalize_entity_type_option(item = {}, index = 0) {
    const normalized_entity_type = {
      id: get_entity_type_id_value(item),
      name: this.get_entity_type_option_label(item, index),
      project_id: get_entity_type_project_id_value(item),
      is_active: item?.is_active !== false
    };
    return normalized_entity_type;
  },

  normalize_entity_type_option_name(name) {
    return String(name || '').trim().toLowerCase();
  },

  has_duplicate_entity_type_id(option, seen_ids) {
    return seen_ids.has(option.id);
  },

  has_duplicate_entity_type_name(option, seen_names) {
    const normalized_name = this.normalize_entity_type_option_name(option.name);
    return Boolean(normalized_name) && seen_names.has(normalized_name);
  },

  remember_entity_type_option(option, seen_ids, seen_names) {
    const normalized_name = this.normalize_entity_type_option_name(option.name);
    seen_ids.add(option.id);
    if (normalized_name) seen_names.add(normalized_name);
  },

  is_unique_entity_type_option(option, seen_ids, seen_names) {
    if (this.has_duplicate_entity_type_id(option, seen_ids)) return false;
    if (this.has_duplicate_entity_type_name(option, seen_names)) return false;
    this.remember_entity_type_option(option, seen_ids, seen_names);
    return true;
  },

  build_entity_type_options(items = []) {
    const unique_options = [];
    const seen_ids = new Set();
    const seen_names = new Set();

    items
      .map((item, index) => this.normalize_entity_type_option(item, index))
      .filter((item) => item.id > 0 && item.is_active)
      .forEach((option) => {
        if (this.is_unique_entity_type_option(option, seen_ids, seen_names)) unique_options.push(option);
      });

    return unique_options;
  },

  async fetch_entity_type_options() {
    if (this.entity_types_loading) return;
    this.entity_types_loading = true;

    try {
      const response = await axios.get(VIEW_ENTITY_TYPE_URL, get_request_config({ params: { page: 1, page_size: 200 } }));
      const response_data = parse_api_data(response.data);
      const items = Array.isArray(response_data.data) ? response_data.data : [];
      this.entity_type_options = this.build_entity_type_options(items);
    } catch {
      this.entity_type_options = [];
    } finally {
      this.entity_types_loading = false;
    }
  },

  async handle_create_role_change() {
    if (!is_coordinator_role(this.create_form.role)) {
      this.create_form.entity_type_id = '';
      return;
    }

    if (!this.entity_type_options.length) await this.fetch_entity_type_options();
  },

  get_selected_create_entity_type() {
    return this.entity_type_options.find((item) => String(item.id) === String(this.create_form.entity_type_id)) || null;
  },

  async handle_edit_role_change() {
    const selected_role = this.roles.find((role) => String(role.id) === String(this.edit_form.role_id));
    if (!is_coordinator_role(selected_role?.role)) {
      this.edit_form.entity_type_id = '';
      return;
    }

    if (!this.entity_type_options.length) await this.fetch_entity_type_options();
  },

  get_selected_edit_entity_type() {
    return this.entity_type_options.find((item) => String(item.id) === String(this.edit_form.entity_type_id)) || null;
  },

  get_selected_edit_entity_type_label() {
    return this.get_selected_edit_entity_type()?.name || this.edit_form.entity_type_name || '';
  },

  should_show_selected_edit_entity_type_fallback() {
    const selected_id = String(this.edit_form.entity_type_id || '').trim();
    if (!selected_id) return false;
    return !this.entity_type_options.some((item) => String(item.id) === selected_id);
  },

  apply_default_create_role() {
    if (this.create_form.role) return;
    this.create_form.role = get_default_role(this.roles);
  },

  async assign_roles() {
    this.roles = await fetch_roles_service();
  },

  async fetch_roles() {
    this.roles_loading = true;

    try {
      await this.assign_roles();
      this.apply_default_create_role();
    } catch {
      this.roles = [];
    } finally {
      this.roles_loading = false;
    }
  },

  async fetch_users() {
    this.is_loading = true;

    try {
      const result = await fetch_users_service();
      this.users = result.users;
      this.pagination = result.pagination;
    } catch {
      this.users = [];
      this.pagination = null;
      this.show_error('Failed to load users.');
    } finally {
      this.is_loading = false;
    }
  },

  get_user_contact_form(form_name) {
    if (form_name === 'create_form') return this.create_form;
    if (form_name === 'edit_form') return this.edit_form;
    return null;
  },

  get_user_name_field_value(form, field_name) {
    if (field_name === 'first_name') return form.first_name;
    if (field_name === 'last_name') return form.last_name;
    return '';
  },

  set_user_name_field_value(form, field_name, value) {
    if (field_name === 'first_name') {
      form.first_name = value;
      return;
    }

    if (field_name === 'last_name') form.last_name = value;
  },

  get_contact_number_field_value(form, field_name) {
    if (field_name === 'phone_number') return form.phone_number;
    if (field_name === 'sms_number') return form.sms_number;
    if (field_name === 'whatsapp_number') return form.whatsapp_number;
    return '';
  },

  set_contact_number_field_value(form, field_name, value) {
    if (field_name === 'phone_number') {
      form.phone_number = value;
      return;
    }

    if (field_name === 'sms_number') {
      form.sms_number = value;
      return;
    }

    if (field_name === 'whatsapp_number') form.whatsapp_number = value;
  },

  validate_create_user() {
    if (!this.create_form.first_name.trim()) return 'First name is required.';
    if (!has_valid_name(this.create_form.first_name)) return 'First name must be at least 2 letters and cannot contain numbers.';
    if (!this.create_form.last_name.trim()) return 'Last name is required.';
    if (!has_valid_name(this.create_form.last_name)) return 'Last name must be at least 2 letters and cannot contain numbers.';
    if (!this.create_form.email.trim()) return 'Email is required.';
    if (!is_valid_email(this.create_form.email)) return 'Please enter a valid email address.';
    if (this.create_form.phone_number.trim() && !is_valid_phone_number(this.create_form.phone_number)) return 'Phone number must be exactly 10 digits.';
    if (this.create_form.sms_number.trim() && !is_valid_phone_number(this.create_form.sms_number)) return 'SMS number must be exactly 10 digits.';
    if (this.create_form.whatsapp_number.trim() && !is_valid_phone_number(this.create_form.whatsapp_number)) return 'WhatsApp number must be exactly 10 digits.';
    if (!this.create_form.role) return 'Role is required.';
    if (is_coordinator_role(this.create_form.role) && !String(this.create_form.entity_type_id || '').trim()) return 'Entity Type is required for the Coordinator role.';
    return '';
  },

  validate_edit_user() {
    if (!this.edit_form.first_name.trim()) return 'First name is required.';
    if (!has_valid_name(this.edit_form.first_name)) return 'First name must be at least 2 letters and cannot contain numbers.';
    if (!this.edit_form.last_name.trim()) return 'Last name is required.';
    if (!has_valid_name(this.edit_form.last_name)) return 'Last name must be at least 2 letters and cannot contain numbers.';
    if (!this.edit_form.email.trim()) return 'Email is required.';
    if (!is_valid_email(this.edit_form.email)) return 'Please enter a valid email address.';
    if (this.edit_form.phone_number.trim() && !is_valid_phone_number(this.edit_form.phone_number)) return 'Phone number must be exactly 10 digits.';
    if (this.edit_form.sms_number.trim() && !is_valid_phone_number(this.edit_form.sms_number)) return 'SMS number must be exactly 10 digits.';
    if (this.edit_form.whatsapp_number.trim() && !is_valid_phone_number(this.edit_form.whatsapp_number)) return 'WhatsApp number must be exactly 10 digits.';
    if (!this.edit_form.role_id) return 'Role is required.';
    const selected_role = this.roles.find((role) => String(role.id) === String(this.edit_form.role_id));
    if (is_coordinator_role(selected_role?.role) && !String(this.edit_form.entity_type_id || '').trim()) return 'Entity Type is required for the Coordinator role.';
    return '';
  },

  open_create_user_modal() {
    if (!this.roles.length) this.fetch_roles();
    if (is_coordinator_role(this.create_form.role) && !this.entity_type_options.length) this.fetch_entity_type_options();
  },

  open_edit_user_modal(user) {
    this.edit_form = this.build_edit_form(user);
    if (!this.roles.length) this.fetch_roles();
    if (String(user.role || '').toLowerCase().includes('coordinator')) this.fetch_entity_type_options();
  },

  build_edit_form(user) {
    return this.$options.build_edit_form(user);
  },

  project_display_name(project) {
    return this.$options.get_project_display_name(project);
  },

  toggle_project_dropdown() {
    if (this.projects_loading || this.assigned_projects_loading) return;
    this.show_project_dropdown = !this.show_project_dropdown;
  },

  select_project(project) {
    const project_id = String(project?.id || '');
    if (!project_id) return;

    const selected_ids = new Set(Array.isArray(this.assign_form.project_ids) ? this.assign_form.project_ids.map(String) : []);
    if (selected_ids.has(project_id)) selected_ids.delete(project_id);
    else selected_ids.add(project_id);
    this.assign_form.project_ids = Array.from(selected_ids);
  },

  is_project_selected(project_id) {
    return (Array.isArray(this.assign_form.project_ids) ? this.assign_form.project_ids : []).map(String).includes(String(project_id));
  }
};
